import { createReducer, createSelector } from '@reduxjs/toolkit';
import { all, call, fork, takeEvery } from 'redux-saga/effects';
import { SUBJECTAREA_INCLUDE } from '../../constants/api';
import {
  createSubjectAreaInitialize,
  createSubjectAreaSuccess,
  deleteSubjectAreaInitialize,
  deleteSubjectAreaSuccess,
  getAllHearingsSuccess,
  getAllSubjectAreasError,
  getAllSubjectAreasInitialize,
  getAllSubjectAreasSuccess,
  getHearingSuccess,
  getPaginatedHearingsSuccess,
  updateSubjectAreaInitialize,
  updateSubjectAreaSuccess,
} from '../actions/api';
import { SubjectAreaConnector } from '../connectors/subjectAreaConnector';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  generateCreateApiCall,
  generateGetAllApiCall,
  generateUpdateApiCall,
} from '../helpers/sagaHelpers';
import {
  getIncludedData,
  getInitActionPayload,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import {
  allHearingsSelector,
  hearingByIdSelector,
  hearingsByIdsSelector,
} from './hearingSlice';

// -------- Overview --------

// The following endpoints exist
//  - GET /subjectArea
//  - GET /subjectArea/{id}
//  - POST /subjectArea
//  - PATCH /subjectArea/{id}
//  - DELETE /subjectArea/{id}
//
// SubjectArea is included in the following endpoints
//  - GET /hearing
//  - GET /hearing/{hearingId}

// -------- Reducer --------
const subjectAreaReducer = createReducer(
  {
    all: [],
    isFetchingSubjectAreas: false,
  },
  (builder) => {
    builder
      .addCase(getAllSubjectAreasInitialize, (state) => {
        state.isFetchingSubjectAreas = true;
      })
      .addCase(getAllSubjectAreasError, (state) => {
        state.isFetchingSubjectAreas = false;
      })
      .addCase(getAllSubjectAreasSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingSubjectAreas: false,
        });
        const subjectAreas = getRequestData(action);
        return updateDtoList(newState, 'all', subjectAreas);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const subjectAreas = data.filter(
          (include) => include.type === SUBJECTAREA_INCLUDE,
        );
        return updateDtoList(state, 'all', subjectAreas);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const subjectAreas = data.filter(
          (include) => include.type === SUBJECTAREA_INCLUDE,
        );
        return updateDtoList(state, 'all', subjectAreas);
      })
      .addCase(updateSubjectAreaSuccess, (state, action) => {
        const subjectArea = getRequestData(action);
        return updateDtoList(state, 'all', subjectArea);
      })
      .addCase(deleteSubjectAreaSuccess, (state, action) => {
        const subjectAreaId = getInitActionPayload(action);
        return removeItemFromList(state, 'all', subjectAreaId);
      })
      .addCase(createSubjectAreaSuccess, (state, action) => {
        const subjectArea = getRequestData(action);
        return updateDtoList(state, 'all', subjectArea);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const subjectAreas = data.filter(
          (include) => include.type === SUBJECTAREA_INCLUDE,
        );
        return updateDtoList(state, 'all', subjectAreas);
      });
  },
);

// -------- Sagas ---------
export function* subjectAreaSaga() {
  yield all([
    fork(getAllSubjectAreasSaga),
    fork(updateSubjectAreaSaga),
    fork(deleteSubjectAreaSaga),
    fork(createSubjectAreaSaga),
  ]);
}

function* getAllSubjectAreasSaga() {
  yield takeEvery(
    getAllSubjectAreasInitialize.type,
    generateGetAllApiCall(SubjectAreaConnector, function* (connector) {
      return yield call([connector, connector.getSubjectAreas]);
    }),
  );
}

function* updateSubjectAreaSaga() {
  yield takeEvery(
    updateSubjectAreaInitialize.type,
    generateUpdateApiCall(SubjectAreaConnector, function* (
      connector,
      action,
      requestData,
    ) {
      return yield call(
        [connector, connector.updateSubjectArea],
        action.payload.subjectAreaId,
        requestData,
      );
    }),
  );
}

function* deleteSubjectAreaSaga() {
  yield takeEvery(
    deleteSubjectAreaInitialize.type,
    generateUpdateApiCall(SubjectAreaConnector, function* (connector, action) {
      return yield call(
        [connector, connector.deleteSubjectArea],
        action.payload,
      );
    }),
  );
}

function* createSubjectAreaSaga() {
  yield takeEvery(
    createSubjectAreaInitialize.type,
    generateCreateApiCall(SubjectAreaConnector, function* (connector, action) {
      return yield call(
        [connector, connector.createSubjectArea],
        action.payload,
      );
    }),
  );
}
// -------- Selectors --------
const selectGetAllSubjectAreas = (state) =>
  selectAllFromApiState(state, 'subjectArea');
const allSubjectAreasSelector = createSelector(
  selectGetAllSubjectAreas,
  (subjectAreas) => subjectAreas,
);
const allSubjectAreaWithNumberOfHearingsSelector = createSelector(
  [allSubjectAreasSelector, allHearingsSelector],
  (subjectAreas, hearings) => {
    const result = [];
    subjectAreas.forEach((subjectArea) => {
      let amount = 0;

      hearings.forEach((hearing) => {
        if (getRelId(hearing, 'subjectArea') === getEntityId(subjectArea)) {
          amount += 1;
        }
      });

      const newSubjectArea = Object.assign({}, subjectArea, {
        attributes: { ...subjectArea.attributes, numOfHearings: amount },
        relationships: { ...subjectArea.relationships },
      });
      result.push(newSubjectArea);
    });
    return result;
  },
);
const subjectAreaByHearingIdSelector = (hearingId) =>
  createSelector(
    [selectGetAllSubjectAreas, hearingByIdSelector(hearingId)],
    (subjectAreas, hearing) =>
      subjectAreas.find(
        (s) => getEntityId(s) === getRelId(hearing, 'subjectArea'),
      ),
  );

const subjectAreasByHearingIdsSelector = (hearingIds) =>
  createSelector(
    [allSubjectAreasSelector, hearingsByIdsSelector(hearingIds)],
    (subjectAreas, hearings) =>
      subjectAreas.filter((subjectArea) =>
        hearings.some(
          (hearing) =>
            getRelId(hearing, 'subjectArea') === getEntityId(subjectArea),
        ),
      ),
  );

const subjectAreaByIdSelector = (subjectAreaId) =>
  createSelector(allSubjectAreasSelector, (subjectAreas) =>
    subjectAreas.find(
      (subjectArea) => getEntityId(subjectArea) === subjectAreaId,
    ),
  );

const numberOfHearingsOnSubjectAreaBySubjectAreaIdSelector = (subjectAreaId) =>
  createSelector(allHearingsSelector, (hearings) => {
    if (!subjectAreaId) {
      return null;
    }
    var result = 0;
    hearings.forEach((hearing) => {
      if (getRelId(hearing, 'subjectArea') === subjectAreaId) {
        result += 1;
      }
    });
    return result;
  });

const subjectAreaState = (state) => state.api.subjectArea;

const isFetchingSubjectAreasSelector = createSelector(
  subjectAreaState,
  (state) => state.isFetchingSubjectAreas,
);
// -------- Exporting --------
export {
  allSubjectAreasSelector,
  subjectAreasByHearingIdsSelector,
  subjectAreaByHearingIdSelector,
  allSubjectAreaWithNumberOfHearingsSelector,
  subjectAreaByIdSelector,
  numberOfHearingsOnSubjectAreaBySubjectAreaIdSelector,
  isFetchingSubjectAreasSelector,
};
export default subjectAreaReducer;
